import React, { useState, useEffect } from 'react';
import { Form, DatePicker, Radio, InputNumber, Select, Row, Col, Typography, Button, Space, Card, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

/**
 * UnifiedDateSection - A clean, unified date component for both invoices and quotations
 * Supports multiple date entries with consistent behavior across all document types
 */
const UnifiedDateSection = ({
  documentType, // 'purchase', 'rental', 'scaffolding_service', 'damaged_items'
  dates = [], // Array of date entries
  onDatesChange, // Callback when dates change
  disabled = false,
  showLegacyInfo = null // For displaying legacy date information
}) => {
  const [form] = Form.useForm();

  // Initialize form fields when dates prop changes
  useEffect(() => {
    if (dates && dates.length > 0) {
      const formValues = {};

      dates.forEach(entry => {
        const { id, type, value, endValue, duration, durationUnit } = entry;

        switch (type) {
          case 'date':
            if (value) {
              formValues[`date_${id}`] = dayjs(value, 'YYYY-MM-DD');
            }
            break;

          case 'range':
            if (value && endValue) {
              formValues[`range_${id}`] = [
                dayjs(value, 'YYYY-MM-DD'),
                dayjs(endValue, 'YYYY-MM-DD')
              ];
            }
            break;

          case 'months':
            if (duration) {
              formValues[`months_${id}`] = duration;
            }
            break;

          case 'duration':
            if (duration) {
              formValues[`duration_${id}`] = duration;
            }
            if (durationUnit) {
              formValues[`durationUnit_${id}`] = durationUnit;
            }
            break;

          case 'tbc':
            // No form fields needed for TBC
            break;
        }
      });

      // Set form values
      form.setFieldsValue(formValues);
    }
  }, [dates, form]);

  // Get available input types for each document type
  const getAvailableInputTypes = (docType) => {
    switch (docType) {
      case 'purchase':
        return [
          { value: 'date', label: 'Delivery Date' },
          { value: 'months', label: 'Lead Time (Months)' },
          { value: 'tbc', label: 'Delivery Date TBC' }
        ];
      case 'scaffolding_service':
        return [
          { value: 'date', label: 'Service Date' },
          { value: 'range', label: 'Service Start & End Date' },
          { value: 'tbc', label: 'Service Date TBC' }
        ];
      case 'rental':
      case 'rental_extension':
        return [
          { value: 'range', label: 'Date Range' },
          { value: 'duration', label: 'Duration' }
        ];
      case 'damaged_items':
        return [
          { value: 'date', label: 'Collection Date' }
        ];
      default:
        return [];
    }
  };

  // Get default input type for document type
  const getDefaultInputType = (docType) => {
    const types = getAvailableInputTypes(docType);
    return types.length > 0 ? types[0].value : 'date';
  };

  // Add new date entry
  const addDateEntry = () => {
    const newEntry = {
      id: Date.now(),
      type: getDefaultInputType(documentType),
      value: null, // ISO string or null
      endValue: null, // For ranges only
      duration: null, // For duration only
      durationUnit: 'days' // For duration only
    };

    const newDates = [...dates, newEntry];
    onDatesChange && onDatesChange(newDates);
  };

  // Remove date entry
  const removeDateEntry = (entryId) => {
    if (dates.length > 1) {
      const newDates = dates.filter(entry => entry.id !== entryId);
      onDatesChange && onDatesChange(newDates);
    }
  };

  // Update date entry with proper cleanup of irrelevant properties
  const updateDateEntry = (entryId, updates) => {
    const newDates = dates.map(entry => {
      if (entry.id === entryId) {
        const updatedEntry = { ...entry, ...updates };

        // Clean up irrelevant properties based on the new type
        if (updates.type) {
          console.log(`🔄 Switching date entry ${entryId} from ${entry.type} to ${updates.type}`);

          switch (updates.type) {
            case 'date':
              // Single date: remove range and duration properties
              if (updatedEntry.endValue || updatedEntry.duration || updatedEntry.durationUnit) {
                console.log(`🧹 Cleaning up single date: removing endValue, duration, durationUnit`);
                updatedEntry.endValue = null;
                updatedEntry.duration = null;
                updatedEntry.durationUnit = null;
              }
              break;

            case 'range':
              // Date range: remove duration properties but keep endValue
              if (updatedEntry.duration || updatedEntry.durationUnit) {
                console.log(`🧹 Cleaning up date range: removing duration, durationUnit`);
                updatedEntry.duration = null;
                updatedEntry.durationUnit = null;
              }
              break;

            case 'months':
              // Lead time in months: remove range and duration unit
              if (updatedEntry.endValue || updatedEntry.durationUnit || updatedEntry.value) {
                console.log(`🧹 Cleaning up months: removing endValue, durationUnit, value`);
                updatedEntry.endValue = null;
                updatedEntry.durationUnit = null;
                updatedEntry.value = null; // Reset value since it's duration-based
              }
              break;

            case 'duration':
              // Duration: remove range properties
              if (updatedEntry.endValue || updatedEntry.value) {
                console.log(`🧹 Cleaning up duration: removing endValue, value`);
                updatedEntry.endValue = null;
                updatedEntry.value = null; // Reset value since it's duration-based
              }
              break;

            case 'tbc':
              // TBC: remove all value-related properties
              if (updatedEntry.value || updatedEntry.endValue || updatedEntry.duration || updatedEntry.durationUnit) {
                console.log(`🧹 Cleaning up TBC: removing all value properties`);
                updatedEntry.value = null;
                updatedEntry.endValue = null;
                updatedEntry.duration = null;
                updatedEntry.durationUnit = null;
              }
              break;
          }

          console.log(`✅ Cleaned date entry:`, {
            id: updatedEntry.id,
            type: updatedEntry.type,
            value: updatedEntry.value,
            endValue: updatedEntry.endValue,
            duration: updatedEntry.duration,
            durationUnit: updatedEntry.durationUnit
          });
        }

        return updatedEntry;
      }
      return entry;
    });

    onDatesChange && onDatesChange(newDates);
  };

  // Render date input based on entry type
  const renderDateInput = (entry) => {
    const { id, type } = entry;

    switch (type) {
      case 'date':
        return (
          <Form.Item
            name={`date_${id}`}
            rules={[{ required: true, message: 'Please select date' }]}
            style={{ margin: 0 }}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Select date"
              format="DD/MM/YYYY"
              disabled={disabled}
              onChange={(date) => {
                updateDateEntry(id, {
                  value: date ? date.format('YYYY-MM-DD') : null
                });
              }}
            />
          </Form.Item>
        );

      case 'range':
        return (
          <Form.Item
            name={`range_${id}`}
            rules={[{ required: true, message: 'Please select date range' }]}
            style={{ margin: 0 }}
          >
            <RangePicker
              style={{ width: '100%' }}
              placeholder={['Start Date', 'End Date']}
              format="DD/MM/YYYY"
              disabled={disabled}
              onChange={(dates) => {
                if (dates && dates.length === 2) {
                  updateDateEntry(id, {
                    value: dates[0].format('YYYY-MM-DD'),
                    endValue: dates[1].format('YYYY-MM-DD')
                  });
                } else {
                  updateDateEntry(id, {
                    value: null,
                    endValue: null
                  });
                }
              }}
            />
          </Form.Item>
        );

      case 'months':
        return (
          <Form.Item
            name={`months_${id}`}
            rules={[{ required: true, message: 'Please enter lead time in months' }]}
            style={{ margin: 0 }}
          >
            <InputNumber
              min={1}
              max={12}
              placeholder="Enter months"
              style={{ width: '100%' }}
              addonAfter="months"
              disabled={disabled}
              onChange={(value) => {
                updateDateEntry(id, { duration: value });
              }}
            />
          </Form.Item>
        );

      case 'duration':
        return (
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item
                name={`duration_${id}`}
                rules={[{ required: true, message: 'Please enter duration' }]}
                style={{ margin: 0 }}
              >
                <InputNumber
                  min={1}
                  placeholder="Enter duration"
                  style={{ width: '100%' }}
                  disabled={disabled}
                  onChange={(value) => {
                updateDateEntry(id, { duration: value });
              }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={`durationUnit_${id}`}
                rules={[{ required: true, message: 'Please select time unit' }]}
                style={{ margin: 0 }}
              >
                <Select
                  placeholder="Select unit"
                  disabled={disabled}
                  onChange={(value) => {
                updateDateEntry(id, { durationUnit: value });
              }}
                >
                  <Option value="days">Days</Option>
                  <Option value="weeks">Weeks</Option>
                  <Option value="months">Months</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        );

      case 'tbc':
        return (
          <div style={{
            padding: '12px',
            backgroundColor: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: '6px',
            textAlign: 'center'
          }}>
            <Text type="secondary">
              Date to be confirmed - No specific date required
            </Text>
          </div>
        );

      default:
        return null;
    }
  };

  // Get card title based on document type and index
  const getCardTitle = (docType, index) => {
    switch (docType) {
      case 'purchase':
        return `Delivery Date Options ${index + 1}`;
      case 'scaffolding_service':
        return `Service Date Options ${index + 1}`;
      case 'rental':
      case 'rental_extension':
        return `Rental Period Options ${index + 1}`;
      case 'damaged_items':
        return `Collection Date Options ${index + 1}`;
      default:
        return `Date Options ${index + 1}`;
    }
  };

  // Get add button text based on document type
  const getAddButtonText = (docType) => {
    switch (docType) {
      case 'purchase':
        return 'Add Another Delivery Date';
      case 'scaffolding_service':
        return 'Add Another Service Date';
      case 'rental':
      case 'rental_extension':
        return 'Add Another Rental Period';
      case 'damaged_items':
        return 'Add Another Collection Date';
      default:
        return 'Add Another Date';
    }
  };

  // Show legacy date information if provided
  if (showLegacyInfo) {
    return (
      <Card title="Legacy Date Information" style={{ marginBottom: 16 }}>
        <Alert
          type="info"
          message="This document uses the legacy date system"
          description={showLegacyInfo}
          showIcon
        />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">
            Legacy dates are preserved for reference. You can update to the new unified date system below.
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <Form form={form} layout="vertical">
      <div className="unified-date-section">
        {dates.map((entry, index) => (
          <Card
            key={entry.id}
            size="small"
            style={{ marginBottom: 8 }}
            title={getCardTitle(documentType, index)}
            extra={
              dates.length > 1 && !disabled && (
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => removeDateEntry(entry.id)}
                />
              )
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* Input type selection */}
              <Radio.Group
                value={entry.type}
                onChange={(e) => updateDateEntry(entry.id, { type: e.target.value })}
                size="small"
                style={{ marginBottom: 8 }}
                disabled={disabled}
              >
                {getAvailableInputTypes(documentType).map(type => (
                  <Radio.Button key={type.value} value={type.value}>
                    {type.label}
                  </Radio.Button>
                ))}
              </Radio.Group>

              {/* Date input based on selected type */}
              {renderDateInput(entry)}
            </Space>
          </Card>
        ))}

        {!disabled && dates.length < 3 && (
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={addDateEntry}
            style={{ width: '100%', marginTop: 8 }}
          >
            {getAddButtonText(documentType)}
          </Button>
        )}
      </div>
    </Form>
  );
};

export default UnifiedDateSection;
